import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { login } from "../../services/api";
import { setAuth, isLoggedIn } from "../../utils/auth";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

const s = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem 1rem",
    background: "#f0f2f5",
  },
  card: {
    background: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: "16px",
    padding: "2.5rem 2rem",
    width: "100%",
    maxWidth: "400px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    marginBottom: "1.75rem",
  },

  brandName: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#1a1a1a",
    letterSpacing: "-0.4px",
  },
  subheading: {
    fontSize: "14px",
    color: "#6b7280",
    marginBottom: "1.75rem",
    textAlign: "center",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "500",
    color: "#374151",
    marginBottom: "6px",
  },
  inputGroup: {
    marginBottom: "1rem",
  },
  input: {
    width: "100%",
    height: "42px",
    padding: "0 12px",
    fontSize: "14px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    background: "#f9fafb",
    color: "#1a1a1a",
    outline: "none",
    transition: "border-color 0.15s, box-shadow 0.15s",
    boxSizing: "border-box",
  },
  inputFocused: {
    borderColor: "#534AB7",
    boxShadow: "0 0 0 3px rgba(83,74,183,0.12)",
    background: "#fff",
  },
  inputError: {
    borderColor: "#E24B4A",
  },
  passwordWrapper: {
    position: "relative",
  },
  eyeBtn: {
    position: "absolute",
    right: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px",
    color: "#9ca3af",
    display: "flex",
    alignItems: "center",
  },
  forgotRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "6px",
    marginBottom: "1.25rem",
  },
  forgotLink: {
    fontSize: "13px",
    color: "#534AB7",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
    fontWeight: "500",
  },
  submitBtn: {
    width: "100%",
    height: "44px",
    background: "#534AB7",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    transition: "background 0.15s, transform 0.1s",
  },
  submitBtnHover: {
    background: "#3C3489",
    transform: "scale(0.99)",
  },
  submitBtnDisabled: {
    opacity: 0.75,
    cursor: "not-allowed",
  },
  errorBanner: {
    color: "#991b1b",
    fontSize: "13px",
    marginBottom: "14px",
    background: "#fee2e2",
    padding: "8px 12px",
    borderRadius: "6px",
  },
  errorText: {
    fontSize: "12px",
    color: "#ef4444",
    marginTop: "4px",
  },
};

export default function Login() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    identifier: "",
    password: "",
    source: "web",
  });
  const [err, setErr] = useState("");
  const [focusedField, setFocusedField] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const [fieldErr, setFieldErr] = useState({ identifier: "", password: "" });

  const validateForm = () => {
    const errors = {
      identifier: "",
      password: "",
    };

    const identifier = form.identifier.trim();
    const password = form.password.trim();

    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);
    const isMobile = /^[6-9]\d{9}$/.test(identifier);

    if (!identifier) {
      errors.identifier = "Email or Mobile is required";
    } else if (!isEmail && !isMobile) {
      errors.identifier = "Enter valid Email or Mobile number";
    }

    if (!password) {
      errors.password = "Password is required";
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    setFieldErr(errors);
    return !errors.identifier && !errors.password;
  };

  useEffect(() => {
    if (isLoggedIn()) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setFieldErr({ ...fieldErr, [name]: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const result = await login(form);

      const success = result.data?.success;
      const message = result.data?.message;
      const data = result.data?.data;

      if (success !== true) {
        setErr(message || "Invalid credentials");
        return;
      }

      setAuth(data.token, data.user);
      navigate("/dashboard");
    } catch (error) {
      setErr(error.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const getInputStyle = (field) => ({
    ...s.input,
    ...(focusedField === field ? s.inputFocused : {}),
    ...(fieldErr[field] ? s.inputError : {}),
  });

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logoRow}>
          <svg
            width="36"
            height="36"
            viewBox="0 0 36 36"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Sales Tracker Logo"
          >
            <rect width="36" height="36" rx="10" fill="#534AB7" />
            <path
              d="M10 25V16"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <path
              d="M18 25V11"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <path
              d="M26 25V19"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <path
              d="M9 26H27"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>

          <span style={s.brandName}>Sales Tracker</span>
        </div>

        <div style={s.subheading}>Sign in to continue</div>

        {err && <div style={s.errorBanner}>{err}</div>}

        <form onSubmit={handleSubmit}>
          <div style={s.inputGroup}>
            <label style={s.label}>Email or Mobile</label>
            <input
              style={getInputStyle("identifier")}
              name="identifier"
              value={form.identifier}
              onChange={handleChange}
              placeholder="Enter Email or Mobile"
              onFocus={() => setFocusedField("identifier")}
              onBlur={() => setFocusedField(null)}
            />
            {fieldErr.identifier && (
              <div style={s.errorText}>{fieldErr.identifier}</div>
            )}
          </div>

          <div style={s.inputGroup}>
            <label style={s.label}>Password</label>
            <div style={s.passwordWrapper}>
              <input
                style={{ ...getInputStyle("password"), paddingRight: "40px" }}
                type={showPwd ? "text" : "password"}
                placeholder="••••••••"
                name="password"
                value={form.password}
                onChange={handleChange}
                onFocus={() => setFocusedField("password")}
                onBlur={() => setFocusedField(null)}
              />

              <button
                style={s.eyeBtn}
                onClick={() => setShowPwd((v) => !v)}
                type="button"
                title={showPwd ? "Hide password" : "Show password"}
              >
                {showPwd ? (
                  <AiOutlineEyeInvisible size={18} color="#9ca3af" />
                ) : (
                  <AiOutlineEye size={18} color="#9ca3af" />
                )}
              </button>
            </div>
            {fieldErr.password && (
              <div style={s.errorText}>{fieldErr.password}</div>
            )}
          </div>

          <div style={s.forgotRow}>
            <button
              style={s.forgotLink}
              type="button"
              onClick={() => navigate("/reset-password")}
            >
              Forgot password?
            </button>
          </div>

          <button
            style={{
              ...s.submitBtn,
              ...(hoveredBtn === "submit" ? s.submitBtnHover : {}),
              ...(loading ? s.submitBtnDisabled : {}),
            }}
            onMouseEnter={() => setHoveredBtn("submit")}
            onMouseLeave={() => setHoveredBtn(null)}
            type="submit"
            disabled={loading}
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
